import { inject } from 'vue';

import { defineStore } from 'pinia';
import {
	SetPersonalUserStateCommand,
	SwitchUserStateCommand,
} from 'src/stores/userOnlineList/commands/SwitchUserStateCommand';
import { USER_ONLINE_DRAWER_WIDTH, USER_ONLINE_MINI_DRAWER_WIDTH } from 'src/stores/userOnlineList/localStorageKeys';
import { GetUserOnlineQuery, GetUserStatesQuery } from 'src/stores/userOnlineList/queries';

import { UserOnlineListItem, UserOnlineListItemRunType, UserState, UserStateRunType } from 'src/models/userOnlineList';

import { QueryStore } from 'src/util/query/QueryStore';

import { UserOnlineListCallback, UserStatesCallback } from './callbacks';
import {
	FetchUserOnlineListPayload,
	FetchUserStatesPayload,
	SetDrawerWidthPayload,
	SetMiniDrawerWidthPayload,
	SetPersonalUserStatePayload,
	SwitchUserStatePayload,
	UnsubscribeFetchUserOnlineListPayload,
	UnsubscribeFetchUserStatesPayload,
	UserOnlineListStoreState,
} from './interfaces';

const readNumberFromStorage = (key: string, defaultValue: number): number => {
	const serializedValue = localStorage.getItem(key);

	if (serializedValue === null) {
		return defaultValue;
	}

	return parseInt(serializedValue);
};

const createDefaultState = (): UserOnlineListStoreState => ({
	drawerWidth: readNumberFromStorage(USER_ONLINE_DRAWER_WIDTH, 240),
	initialLoadingFinished: false,
	miniDrawerWidth: readNumberFromStorage(USER_ONLINE_MINI_DRAWER_WIDTH, 54),
	userOnlineList: [],
	userStates: [],
});

export const useUserOnlineListStore = defineStore('userOnlineList', {
	actions: {
		fetchUserOnlineList(payload: FetchUserOnlineListPayload): void {
			if (payload.queryStore !== undefined) {
				void payload.queryStore.query<UserOnlineListItem[]>({
					callback: new UserOnlineListCallback(
						(state) => this.setInitialLoadingFinished(state),
						(userOnlineList) => this.setUserOnlineList(userOnlineList)
					),
					createTime: new Date(),
					query: new GetUserOnlineQuery(),
					responseType: 'json',
					runType: UserOnlineListItemRunType,
				});
				return;
			}

			this.setUserOnlineList([]);
		},
		fetchUserStates(payload: FetchUserStatesPayload): void {
			if (payload.queryStore !== undefined) {
				void payload.queryStore.query<UserState[]>({
					callback: new UserStatesCallback((userStates) => this.setUserStates(userStates)),
					createTime: new Date(),
					query: new GetUserStatesQuery(),
					responseType: 'json',
					runType: UserStateRunType,
				});
				return;
			}

			this.setUserStates([]);
		},
		setDrawerWidth(payload: SetDrawerWidthPayload): void {
			localStorage.setItem(USER_ONLINE_DRAWER_WIDTH, String(payload.drawerWidth));
			this.drawerWidth = payload.drawerWidth;
		},
		setInitialLoadingFinished(payload: boolean): void {
			this.initialLoadingFinished = payload || false;
		},
		setMiniDrawerWidth(payload: SetMiniDrawerWidthPayload): void {
			localStorage.setItem(USER_ONLINE_MINI_DRAWER_WIDTH, String(payload.miniDrawerWidth));
			this.miniDrawerWidth = payload.miniDrawerWidth;
		},
		setPersonalUserState(payload: SetPersonalUserStatePayload): void {
			void payload.commandBus?.sendCommand(new SetPersonalUserStateCommand(payload.text)).then(() => {
				void payload.commandBus?.sendCommand(new SwitchUserStateCommand(payload.userStateId));
			});
		},
		setUserOnlineList(payload: Array<UserOnlineListItem>): void {
			this.userOnlineList = payload || [];
		},
		setUserStates(payload: Array<UserState>): void {
			this.userStates = payload || [];
		},
		switchUserState(payload: SwitchUserStatePayload): void {
			void payload.commandBus?.sendCommand(new SwitchUserStateCommand(payload.userStateId));
		},
		unsubscribeFetchUserOnlineList(payload?: UnsubscribeFetchUserOnlineListPayload): void {
			const queryStore = payload?.queryStore ?? inject<QueryStore>('queryStore');

			if (queryStore !== undefined) {
				void queryStore.unsubscribeQuery(new GetUserOnlineQuery());
			}
		},
		unsubscribeFetchUserStates(payload?: UnsubscribeFetchUserStatesPayload): void {
			const queryStore = payload?.queryStore ?? inject<QueryStore>('queryStore');

			if (queryStore !== undefined) {
				void queryStore.unsubscribeQuery(new GetUserStatesQuery());
			}
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
